/**
 * HONNAN JÖTT A LÁTOGATÓ — ÉS MELYIK HIRDETÉSBŐL.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIÉRT NEM AZ ANALITIKÁRA BÍZZUK
 *
 * Az `analytics.ts` `utm()` segédje a PILLANATNYI URL-t olvassa, és az
 * eseményhez csatolja. Ez a hirdetésmérésre nem elég: a látogató a
 * hirdetésből az `/arbecsles` oldalra érkezik, ott végignavigál a
 * varázslón, és mire elküldi az ajánlatkérést, a query string rég
 * eltűnt. Az esemény akkor már utm nélkül megy.
 *
 * Itt ezért a BEÉRKEZÉSKOR mentünk, és a mentett értéket adjuk az
 * ajánlatkéréshez — akárhány kattintással később.
 *
 * ────────────────────────────────────────────────────────────────────
 * ELSŐ ÉS UTOLSÓ ÉRINTÉS — MINDKETTŐ
 *
 * `first`: melyik hirdetés hozta be EGYÁLTALÁN. Ez mondja meg, mi
 *          nyitja meg a kaput.
 * `last`:  melyik hirdetésre kattintott LEGUTÓBB, mielőtt megrendelt.
 *          A fizetett méréseket ez alapján szokás elszámolni, és a
 *          `v_ad_performance` nézet is ezt használja.
 *
 * A kettő ugyanannyiba kerül eltárolni, és külön kérdésre válaszol.
 *
 * ────────────────────────────────────────────────────────────────────
 * HOZZÁJÁRULÁS: EZ NEM MÉRÉSI SÜTI
 *
 * Ez az adat a MEGRENDELÉS RÉSZE — ugyanúgy, ahogy az, hogy a vevő
 * milyen anyagot választott. Nem harmadik félnek megy, nem profilozunk
 * vele, és a látogató azonosítására sem alkalmas: egyetlen
 * hirdetésazonosító és néhány kampánycímke. A tárolás jogalapja a
 * szerződés teljesítése, nem a hozzájárulás.
 *
 * A Meta pixel EZTŐL FÜGGETLENÜL a hozzájárulás szerint fut
 * (`consent.ts`) — ez a modul nem hívja és nem kerüli meg.
 * ────────────────────────────────────────────────────────────────────
 */

const KULCS = 'voxora_attr_v1'

/**
 * 30 nap. Ugyanaz az ablak, amit a Meta a kattintás-alapú
 * hozzárendelésnél a leghosszabb beállításként enged, tehát a saját
 * számunk és a Meta száma ugyanarra az időszakra vonatkozik.
 */
const ELETTARTAM_MS = 30 * 24 * 60 * 60 * 1000

export interface Erintes {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  /** A Meta hirdetésazonosítója; a `{{ad.id}}` helyére kerül kattintáskor. */
  fbadid: string | null
  /** Mikor érkezett — ISO dátum. */
  ts: string
}

export interface Attribucio {
  first: Erintes
  last: Erintes
  /** Melyik oldalra érkezett. Csak az út, query nélkül. */
  landing_path: string
}

/*
  A hirdetésből érkező látogatót EGYETLEN dolog azonosítja biztosan: a
  `fbadid`. A többi címke akkor is ott lehet, ha valaki kézzel másolt
  egy linket — de az sem baj, a nézet a `fbadid`-re csoportosít.
*/
const MEZOK = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'] as const

/**
 * A HOSSZKORLÁT NEM ÓVATOSSÁG, HANEM VÉDEKEZÉS.
 *
 * Az érték a címsorból jön, tehát a látogató (vagy bárki, aki linket
 * küld neki) tetszőleges hosszú szemetet írhat bele. 120 karakter
 * fölött biztosan nem valódi kampánycímke.
 */
function tisztit(ertek: string | null): string | null {
  if (!ertek) return null
  const t = ertek.trim()
  if (!t || t.length > 120) return null
  return t
}

function erintesAzUrlbol(): Erintes | null {
  try {
    const p = new URLSearchParams(location.search)

    const fbadid = tisztit(p.get('fbadid'))
    const cimkek = Object.fromEntries(
      MEZOK.map((m) => [m, tisztit(p.get(m))])
    ) as Record<(typeof MEZOK)[number], string | null>

    /*
      Ha SEMMILYEN címke nincs az URL-ben, ez nem egy új érintés — a
      látogató csak lapozott egyet az oldalon belül. Ilyenkor a korábban
      mentett `last` érték érvényben marad.
    */
    const vanBarmi = fbadid !== null || MEZOK.some((m) => cimkek[m] !== null)
    if (!vanBarmi) return null

    return { ...cimkek, fbadid, ts: new Date().toISOString() }
  } catch {
    return null
  }
}

function olvas(): Attribucio | null {
  try {
    const nyers = localStorage.getItem(KULCS)
    if (!nyers) return null

    const a = JSON.parse(nyers) as Attribucio
    if (!a?.first?.ts || !a?.last?.ts) return null

    // Lejárt eredet: inkább SEMMI, mint egy hónapnál régebbi hamis kapcsolat.
    if (Date.now() - Date.parse(a.first.ts) > ELETTARTAM_MS) {
      localStorage.removeItem(KULCS)
      return null
    }
    return a
  } catch {
    return null
  }
}

/**
 * BEÉRKEZÉS RÖGZÍTÉSE. Az alkalmazás indulásakor fut, egyszer.
 *
 * SOSEM DOB. Ha a tárolás nem elérhető (privát ablak, letiltott
 * tárhely), a mérés csendben kimarad — egy hirdetéscímke nem
 * akadályozhatja meg a rendelést.
 */
export function rogzitBeerkezes(): void {
  try {
    const uj = erintesAzUrlbol()
    if (!uj) return

    const meglevo = olvas()
    const rekord: Attribucio = meglevo
      ? { ...meglevo, last: uj }
      : { first: uj, last: uj, landing_path: location.pathname }

    localStorage.setItem(KULCS, JSON.stringify(rekord))
  } catch {
    // Szándékosan néma.
  }
}

/** A mentett eredet, vagy `null`, ha a látogató nem hirdetésből jött. */
export function attribucio(): Attribucio | null {
  return olvas()
}

/**
 * Az adatkezelési tájékoztató ígéri, hogy a böngésző tárhelyének
 * törlésével az összefűzés megszakítható. Ez ugyanazt teszi egy
 * lépésben — az `analytics.ts` `forgetVisit()` párja.
 */
export function felejtsdAzEredetet(): void {
  try {
    localStorage.removeItem(KULCS)
  } catch {
    // Ha eleve nincs tárolás, nincs mit törölni.
  }
}
